import { useEffect, useRef } from 'react';
import type { ChatMessage } from './types';
import { cn } from '../../utils/cn';

interface Props {
  messages: ChatMessage[];
  selfUserId: string;
  onLoadOlder?: () => void;
  hasMore?: boolean;
  loadingOlder?: boolean;
}

export function MessageList({ messages, selfUserId, onLoadOlder, hasMore, loadingOlder }: Props) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastLenRef = useRef(messages.length);

  useEffect(() => {
    if (!scrollRef.current) return;
    if (messages.length > lastLenRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    lastLenRef.current = messages.length;
  }, [messages.length]);

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto px-4 py-3 space-y-2"
      data-test-id="chat-message-list"
    >
      {hasMore && (
        <button
          type="button"
          onClick={onLoadOlder}
          className="block mx-auto text-xs text-gray-500 hover:text-gray-700"
          disabled={loadingOlder}
        >
          {loadingOlder ? 'Loading…' : 'Load older messages'}
        </button>
      )}
      {messages.length === 0 && !hasMore && (
        <div className="text-center text-xs text-gray-400 py-6">No messages yet.</div>
      )}
      {messages.map((m) => {
        const mine = m.sender_user_id === selfUserId;
        return (
          <div key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
            <div
              className={cn(
                'max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words shadow-sm',
                mine ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-900',
              )}
            >
              {m.body}
              <div className={cn('text-[10px] mt-1', mine ? 'text-orange-100' : 'text-gray-500')}>
                {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
